
import React from 'react';
import { format, parseISO } from 'date-fns';
import { CarTaxiFront, CalendarPlus } from 'lucide-react';
import { TripElement } from '@/types/trip';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { trackTimelineAction } from '@/utils/analytics';

interface CarRentalElementProps {
  element: TripElement;
  tripId: string;
  destinationName: string;
}

export const CarRentalElement: React.FC<CarRentalElementProps> = ({ 
  element, 
  tripId, 
  destinationName 
}) => {
  const { t } = useTranslation();
  const carDetails = element.details as any;

  const isAppleDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|mac|macintosh/.test(userAgent);
  };

  const addToCalendar = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    
    const startDate = parseISO(carDetails.pickupTime || element.startDate || "1970");
    const endDate = parseISO(carDetails.dropoffTime || element.endDate || "1970");
    
    const title = `${t("tripCard.carRental", "Car Rental")}: ${carDetails.company}`;
    const description = `${t("tripCard.carModel", "Car Model")}: ${carDetails.model}
${t("tripCard.pickupLocation", "Pickup")}: ${carDetails.pickupLocation}
${t("tripCard.dropoffLocation", "Dropoff")}: ${carDetails.dropoffLocation}
${carDetails.confirmationNumber ? `${t("tripCard.confirmationNumber", "Confirmation Number")}: ${carDetails.confirmationNumber}` : ''}`;
    
    // Track the calendar add action
    trackTimelineAction(
      "add_to_calendar", 
      "car", 
      tripId, 
      destinationName
    );
    
    if (isAppleDevice()) {
      openAppleCalendar(title, description, startDate, endDate, carDetails.pickupLocation);
    } else {
      openGoogleCalendar(title, description, startDate, endDate, carDetails.pickupLocation);
    }
  };

  const openAppleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for Apple Calendar URL
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    
    // Create Apple Calendar URL with all parameters (webcal protocol)
    const calendarUrl = `webcal://p30-calendarws.icloud.com/ca/subscribe/1/${encodeURIComponent(title)}?${
      new URLSearchParams({
        title: encodeURIComponent(title),
        desc: encodeURIComponent(description),
        location: encodeURIComponent(location),
        dtstart: formattedStartDate,
        dtend: formattedEndDate
      }).toString()
    }`;
    
    // Open calendar URL
    window.location.href = calendarUrl;
  };

  const openGoogleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for calendar URL (remove special characters)
    const formattedStartDate = startDate.toISOString().replace(/-|:|\.\d+/g, '');
    const formattedEndDate = endDate.toISOString().replace(/-|:|\.\d+/g, '');
    
    // Create calendar URL with all parameters
    const calendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formattedStartDate}/${formattedEndDate}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&sprop=&sprop=name:`;
    
    // Open calendar URL
    window.open(calendarUrl, '_blank');
  };

  return (
    <div className="bg-travel-50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-travel-600">
        <CarTaxiFront className="w-4 h-4" />
        <span className="font-semibold text-sm">
          {carDetails.company} - {carDetails.model}
        </span>
      </div>
      <div className="text-sm text-travel-500">
        {carDetails.pickupLocation}
      </div>
      <div className="flex justify-between text-xs text-travel-500">
        <div className="flex flex-col">
          <span className="font-medium">{t("tripCard.pickup", "Pickup")}:</span>
          <span>{format(parseISO(carDetails.pickupTime || element.startDate || "1970"), 'MMM d, HH:mm')}</span>
        </div>
        <div className="flex flex-col text-right">
          <span className="font-medium">{t("tripCard.dropoff", "Dropoff")}:</span>
          <span>{format(parseISO(carDetails.dropoffTime || element.endDate || "1970"), 'MMM d, HH:mm')}</span>
        </div>
      </div>
      {carDetails.confirmationNumber && (
        <div className="text-xs text-travel-500">
          <span className="font-medium">{t("tripCard.confirmationNumber", "Confirmation Number")}: </span>
          <span>{carDetails.confirmationNumber}</span>
        </div>
      )}
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full mt-2 bg-white text-travel-600 border-travel-200 hover:bg-travel-50 hover:text-travel-700"
        onClick={addToCalendar}
      >
        <CalendarPlus className="mr-1 h-4 w-4" />
        {t("tripCard.addToCalendar", "Add to Calendar")}
      </Button>
    </div>
  );
};
